import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Space, Typography } from 'antd';
import { publicApi } from '../../services/apiInstances';

const { Text } = Typography;

/**
 * WP-10 slice 5 — offer an account once the estimate is approved.
 *
 *   "still need this page mainly for approval and customer facing things like
 *    customer account creation."
 *
 * ── This is the customer-facing half of a contract WP-04 already built ────
 *
 * `PUT/GET /:token/registration-draft` and the `portal_pending_registrations`
 * table have existed since WP-04, mounted on this very router
 * (`mountPortalCustomerDataRoutes(router, 'estimate')`), gated by
 * `stage_registration`. Nothing on the customer's screen ever called either
 * one, so — like `note` in slice 2 and the completeness prompt in slice 3 — the
 * feature was decided and unreachable. This component reaches it.
 *
 * ── The draft carries DATA and never AUTHORITY ────────────────────────────
 *
 * The banked draft is a convenience: it remembers what the customer typed so
 * they do not retype it. It cannot create an account, and THE PASSWORD IS NEVER
 * BANKED — it is held in component state and sent only to
 * `POST /api/customer-auth/register`.
 *
 * Account creation still goes through that endpoint with the portal token, and
 * WP-04's I-1 check is untouched: claiming an EXISTING customer record requires
 * the link the shop sent. A passed vehicle challenge pre-fills this form, it
 * does not become proof of who you are — wiring it the other way would make a
 * guessable challenge sufficient to take over a customer record, a strictly
 * worse hole than the one WP-04 closed.
 */

export interface PostApprovalRegistrationProps {
  token: string;
  /** The customer this document belongs to, so the account CLAIMS that record. */
  customerId?: number | null;
  /** Hidden entirely unless the document has been approved. */
  approved: boolean;
  /**
   * What the page already knows about this customer, used to prefill the form.
   *
   * The banked draft (`GET /:token/registration-draft`) was the only prefill
   * source and it is empty by default: banking it needs the `stage_registration`
   * portal action, which compatibility mode — `portal_document_lock_enabled = 0`,
   * the shipped default — does not grant to an anonymous link-holder. The PUT
   * 403s, the component swallows it by design, and the customer was left retyping
   * the name, email and phone they had entered during the approval seconds before.
   *
   * The estimate payload already carries these values, so the page passes them.
   * A banked draft still wins when there is one: it is the customer's own more
   * recent typing.
   *
   * Never carries a password. There is nothing to prefill one from.
   */
  prefill?: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
}

/** Blank, whitespace, null and undefined are all "no value here". */
function presentOnly(source: PostApprovalRegistrationProps['prefill']): Partial<DraftFields> {
  const out: Partial<DraftFields> = {};
  for (const key of ['first_name', 'last_name', 'email', 'phone'] as const) {
    const raw = source?.[key];
    if (typeof raw === 'string' && raw.trim() !== '') out[key] = raw.trim();
  }
  return out;
}

interface DraftFields {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const EMPTY: DraftFields = { first_name: '', last_name: '', email: '', phone: '' };

export const PostApprovalRegistration: React.FC<PostApprovalRegistrationProps> = ({
  token,
  customerId,
  approved,
  prefill,
}) => {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<DraftFields>(EMPTY);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /** Pre-fill from whatever was banked earlier — the whole point of the draft. */
  const loadDraft = useCallback(async () => {
    // Start from what the page already knows. Without this the form opens blank
    // whenever the draft write was refused — which is the DEFAULT, because
    // banking a draft needs `stage_registration` and compatibility mode does not
    // grant it. The customer was retyping what they had entered moments earlier.
    const fromPage = presentOnly(prefill);
    setFields((prev) => ({ ...prev, ...fromPage }));

    try {
      const res = await publicApi.get(`/public/estimates/${token}/registration-draft`);
      const d = res.data?.draft ?? res.data;
      if (d) {
        // A banked draft WINS over the page values: it is the customer's own more
        // recent typing. Field by field, so a partial draft does not blank the
        // rest.
        setFields((prev) => ({
          ...prev,
          ...presentOnly({
            first_name: d.first_name,
            last_name: d.last_name,
            email: d.email,
            phone: d.phone,
          }),
        }));
      }
    } catch {
      // Non-fatal: an absent draft is the normal first-visit case, and a failed
      // read must not cost the customer the offer of an account. The page values
      // set above still stand.
    }
  }, [token, prefill]);

  useEffect(() => {
    if (approved && open) loadDraft();
  }, [approved, open, loadDraft]);

  /**
   * Bank what has been typed so far. Fired on blur, so a customer who gets
   * halfway through and closes the tab does not lose it.
   *
   * Deliberately silent on failure: this is a convenience running while
   * somebody moves between fields, and an error toast there interrupts them
   * over something that costs nothing.
   */
  const bank = async (next: DraftFields) => {
    try {
      // No password. Ever.
      await publicApi.put(`/public/estimates/${token}/registration-draft`, next);
    } catch {
      /* convenience only */
    }
  };

  const setField = (key: keyof DraftFields, value: string) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    setError(null);
    if (!fields.email && !fields.phone) {
      setError('Please give us an email address or a phone number.');
      return;
    }
    if (password.length < 8) {
      setError('Please choose a password of at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await publicApi.post('/customer-auth/register', {
        email: fields.email || undefined,
        phone: fields.phone || undefined,
        password,
        firstName: fields.first_name || undefined,
        lastName: fields.last_name || undefined,
        // Claiming the EXISTING customer record this document belongs to.
        // WP-04's I-1 check requires the portal token for exactly this, and
        // that requirement is untouched here.
        customerId: customerId ?? undefined,
        portalToken: token,
      });
      setDone(true);
      // The password never persists beyond the request that used it.
      setPassword('');
    } catch (e: any) {
      // WP-03 keeps `error` a plain string, so it is safe to render directly.
      setError(e?.response?.data?.error || 'We could not create the account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Nothing to offer before the customer has actually approved anything.
  if (!approved) return null;

  if (done) {
    return (
      <Card style={{ marginBottom: 16 }} data-testid="post-approval-registration">
        <Alert
          type="success"
          showIcon
          message="Your account is ready"
          description="You can now sign in to see your history, invoices and receipts."
        />
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 16 }} data-testid="post-approval-registration">
      {!open ? (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Text strong style={{ fontSize: 16 }}>Create an account?</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Keep your service history, invoices and receipts in one place. We already
            have your details — it takes a moment.
          </Text>
          <Button
            type="primary"
            block
            size="large"
            onClick={() => setOpen(true)}
            data-testid="registration-open"
          >
            Set up my account
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Text strong style={{ fontSize: 16 }}>Create your account</Text>
          {error && (
            <Alert type="error" showIcon message={error} data-testid="registration-error" />
          )}

          {([
            ['first_name', 'First name', 'text'],
            ['last_name', 'Last name', 'text'],
            ['email', 'Email', 'email'],
            ['phone', 'Phone', 'tel'],
          ] as Array<[keyof DraftFields, string, string]>).map(([key, label, type]) => (
            <div key={key}>
              <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
              <Input
                data-testid={`registration-${key}`}
                aria-label={label}
                type={type}
                value={fields[key]}
                onChange={(e) => setField(key, e.target.value)}
                onBlur={() => bank(fields)}
                // 16px keeps mobile Safari from zooming the viewport on focus.
                style={{ fontSize: 16 }}
              />
            </div>
          ))}

          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Password</Text>
            <Input.Password
              data-testid="registration-password"
              aria-label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              // NO onBlur. The password is never banked into the draft — it is
              // held here and sent only to the registration endpoint.
              style={{ fontSize: 16 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>At least 8 characters.</Text>
          </div>

          <Button
            type="primary"
            block
            size="large"
            loading={submitting}
            onClick={submit}
            data-testid="registration-submit"
          >
            Create account
          </Button>
        </Space>
      )}
    </Card>
  );
};

export default PostApprovalRegistration;
